import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePostHog, useFeatureFlagEnabled } from 'posthog-js/react';
import { authClient, signInWithGoogle } from '../lib/auth-client';
import './login.css';

const Login: React.FC = () => {
  const posthog = usePostHog();
  const isGoogleAuthEnabled = useFeatureFlagEnabled('google-auth');
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return;

    setIsGoogleLoading(true);
    setError(null);

    try {
      posthog?.capture('auth_google_signin_started', {
        route: returnTo || '/',
      });

      await signInWithGoogle(returnTo);
      // This will redirect to Google, so we won't reach code after this
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Failed to start Google sign-in. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;

    // Read from the DOM rather than React state because some browsers
    // (notably Safari/iOS) autofill the input without firing onChange.
    const formData = new FormData(e.currentTarget);
    const rawEmail = (formData.get('email') as string | null) ?? '';
    if (rawEmail !== email) setEmail(rawEmail);

    setIsLoading(true);
    setError(null);

    const normalizedEmail = rawEmail.trim().toLowerCase();

    try {
      // Use absolute URL so after backend verification, user is redirected to frontend
      const callbackURL = `${window.location.origin}/auth/callback?returnTo=${encodeURIComponent(returnTo)}`;

      // Store email for "request new link" flow
      sessionStorage.setItem('magicLinkEmail', normalizedEmail);

      const { error: authError } = await authClient.signIn.magicLink({
        email: normalizedEmail,
        callbackURL,
      });

      if (authError) {
        // Check for rate limit error
        if (authError.status === 429) {
          setError('Too many requests. Please try again later.');
        } else {
          setError(authError.message || 'Something went wrong');
        }
        setIsLoading(false);
        return;
      }

      posthog?.capture('auth_magic_link_requested', {
        route: returnTo || '/',
        is_returning: false
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Login error:', err);
      setError('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login">
        <main className="login__container">
          <h1 className="login__title">CHECK YOUR EMAIL</h1>
          <p className="login__message">
            If an account exists for this email, you'll receive a login link shortly.
          </p>
          <p className="login__hint">
            The link expires in 15 minutes. Check your spam folder if you don't see it.
          </p>
          <Link to="/" className="login__link">Back to Home</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="login">
      <main className="login__container">
        <h1 className="login__title">LOG IN</h1>
        {isGoogleAuthEnabled && (
          <p className="login__subtitle">Choose your sign-in method</p>
        )}

        {isGoogleAuthEnabled && (
          <>
            <button
              type="button"
              className="login__button login__button--google"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading || isLoading}
            >
              <svg className="login__google-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isGoogleLoading ? 'Redirecting...' : 'Sign in with Google'}
            </button>

            <div className="login__divider">
              <span className="login__divider-text">or</span>
            </div>
          </>
        )}

        {isGoogleAuthEnabled && <p className="login__form-label">Sign in with email</p>}
        <form onSubmit={handleSubmit} className="login__form">
          <label htmlFor="email" className="login__label">Email address</label>
          <input
            id="email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="login__input"
            autoFocus
            required
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={isLoading || isGoogleLoading}
          />
          <button type="submit" className="login__button" disabled={isLoading || isGoogleLoading}>
            {isLoading ? 'Sending...' : 'Send Magic Link'}
          </button>
        </form>

        {error && <p className="login__error">{error}</p>}

        <Link to="/" className="login__link">Back to Home</Link>
      </main>
    </div>
  );
};

export default Login;
